import { FundingSourceDeleteDialogs } from '@client/components/FundingSourceDeleteDialogs';
import { useAddPolicyToLtcPlanning } from '@client/hooks/useAddPolicyToLtcPlanning';
import { useFundingSourceDelete } from '@client/hooks/useFundingSourceDelete';
import type { TraditionalLTCQuoteWithROI } from '@client/hooks/useTraditionalLTCQuotesWithROI';
import { selectClient } from '@client/model';
import type { Quote } from '@client/model/quotesModel';
import {
  getShopInsuranceQuoteIds,
  isPolicyAddedViaShopInsurance,
} from '@client/util/policyFundingSourceUtils';
import { isTraditionalLTCQuote } from '@client/util/quoteTypeGuards';
import { groupBy } from 'lodash';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { PolicyQuoteCard } from './PolicyQuoteCard';

interface QuoteWithROI extends Quote {
  roi?: number;
  formattedROI?: string;
  roiValues?: any;
  projectedCostCoverage?: number;
}

type PolicyQuote = QuoteWithROI | TraditionalLTCQuoteWithROI;

interface PolicyQuoteCardsViewProps {
  lifeInsuranceQuotes: QuoteWithROI[];
  traditionalLTCQuotes: TraditionalLTCQuoteWithROI[];
  clientPartner?: Client | null;
  filterMode?: 'selected' | 'unselected';
  emptyMessage?: {
    title: string;
    description: string;
  };
}

export function PolicyQuoteCardsView({
  lifeInsuranceQuotes,
  traditionalLTCQuotes,
  clientPartner = null,
  filterMode = 'unselected',
  emptyMessage,
}: PolicyQuoteCardsViewProps) {
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const client = useSelector(selectClient);

  const { addPolicy } = useAddPolicyToLtcPlanning({ clientPartner });
  const {
    deletePolicyByFundingSourceIdCallBack,
    showDeleteConfirmation,
    showDeleteBlockedDialog,
    setShowDeleteBlockedDialog,
    deleteBlockedReason,
    showJointPolicyDeleteDialog,
    jointPolicyDeleteMode,
    setJointPolicyDeleteMode,
    handleDeleteClick,
    handleCancelDelete,
    handleConfirmDelete,
    handleJointPolicyDeleteCancel,
    handleJointPolicyDeleteConfirm,
  } = useFundingSourceDelete(client, []);

  // Get all shop insurance quote IDs for efficient lookup
  const shopInsuranceQuoteIds = useMemo(
    () => getShopInsuranceQuoteIds(client?.multipleFundingSources),
    [client?.multipleFundingSources],
  );

  // Helper function to get quote ID
  const getQuoteId = (quote: QuoteWithROI | TraditionalLTCQuoteWithROI): string => {
    return 'insuranceQuoteId' in quote ? quote.insuranceQuoteId : quote.traditionalLTCQuoteId;
  };

  // Check if a quote is in multipleFundingSources with addedViaShopInsurance
  const isQuoteSelected = (
    quote: QuoteWithROI | TraditionalLTCQuoteWithROI,
  ): {
    isSelected: boolean;
    fundingSourceId: string | null;
  } => {
    const quoteId = getQuoteId(quote);
    const isSelected = shopInsuranceQuoteIds.has(quoteId);

    if (!isSelected) {
      return { isSelected: false, fundingSourceId: null };
    }

    // If selected, find the funding source ID
    if (client?.multipleFundingSources?.ltcPolicy) {
      for (const [fundingSourceId, policy] of Object.entries(
        client.multipleFundingSources.ltcPolicy,
      )) {
        if (isPolicyAddedViaShopInsurance(policy) && policy.insuranceQuoteId === quoteId) {
          return { isSelected: true, fundingSourceId };
        }
      }
    }

    return { isSelected: false, fundingSourceId: null };
  };

  const parseStoredQuote = (
    storedQuote: unknown,
  ): QuoteWithROI | TraditionalLTCQuoteWithROI | null => {
    if (!storedQuote) return null;
    if (typeof storedQuote === 'string') {
      try {
        return JSON.parse(storedQuote);
      } catch {
        return null;
      }
    }
    return storedQuote as QuoteWithROI | TraditionalLTCQuoteWithROI;
  };

  const filteredLifeInsuranceQuotes = useMemo(() => {
    if (filterMode === 'selected') {
      if (!client?.multipleFundingSources?.ltcPolicy) return [];

      return Object.values(client.multipleFundingSources.ltcPolicy)
        .filter(policy => isPolicyAddedViaShopInsurance(policy))
        .map(policy => {
          const storedQuote = parseStoredQuote(policy.updatedQuote || policy.sourceQuote);
          if (!storedQuote || isTraditionalLTCQuote(storedQuote)) return null;
          return storedQuote as QuoteWithROI;
        })
        .filter((q): q is QuoteWithROI => q !== null);
    }

    if (filterMode === 'unselected') {
      return lifeInsuranceQuotes.filter(quote => !shopInsuranceQuoteIds.has(getQuoteId(quote)));
    }
    return lifeInsuranceQuotes;
  }, [
    client?.multipleFundingSources?.ltcPolicy,
    lifeInsuranceQuotes,
    filterMode,
    shopInsuranceQuoteIds,
  ]);

  const filteredTraditionalLTCQuotes = useMemo(() => {
    if (filterMode === 'selected') {
      if (!client?.multipleFundingSources?.ltcPolicy) return [];

      return Object.values(client.multipleFundingSources.ltcPolicy)
        .filter(policy => isPolicyAddedViaShopInsurance(policy))
        .map(policy => {
          const storedQuote = parseStoredQuote(policy.updatedQuote || policy.sourceQuote);
          if (!storedQuote || !isTraditionalLTCQuote(storedQuote)) return null;
          return storedQuote as TraditionalLTCQuoteWithROI;
        })
        .filter((q): q is TraditionalLTCQuoteWithROI => q !== null);
    }

    if (filterMode === 'unselected') {
      return traditionalLTCQuotes.filter(quote => !shopInsuranceQuoteIds.has(getQuoteId(quote)));
    }
    return traditionalLTCQuotes;
  }, [
    client?.multipleFundingSources?.ltcPolicy,
    traditionalLTCQuotes,
    filterMode,
    shopInsuranceQuoteIds,
  ]);

  // Group life insurance quotes by carrier AND policy type
  // This way we show separate cards for "OneAmerica Single Life" vs "OneAmerica Joint Life"
  const lifeInsuranceByCarrier = useMemo(() => {
    // Group by both carrier AND policy type to keep them separate
    const grouped = groupBy(
      filteredLifeInsuranceQuotes,
      quote => `${quote.carrierName}-${quote.policyType}`,
    );

    return Object.entries(grouped)
      .map(([key, quotes]) => {
        const sortedByROI = [...quotes].sort((a, b) => (b.roi ?? 0) - (a.roi ?? 0));
        return {
          carrierName: sortedByROI[0].carrierName,
          topQuote: sortedByROI[0],
          allQuotes: sortedByROI,
          policyType: sortedByROI[0].policyType,
        };
      })
      .sort((a, b) => (b.topQuote.roi ?? 0) - (a.topQuote.roi ?? 0));
  }, [filteredLifeInsuranceQuotes]);

  // Traditional LTC quotes - group by carrier AND maritalClass
  // This way we show separate cards for "CareScout Single" vs "CareScout Married 1 Buy" vs "CareScout Married 2 Buy"
  const traditionalLTCByCarrierAndMaritalClass = useMemo(() => {
    const grouped = groupBy(
      filteredTraditionalLTCQuotes,
      quote => `${quote.carrier}-${quote.maritalClass}`,
    );

    return Object.entries(grouped)
      .map(([key, quotes]) => {
        const sortedByROI = [...quotes].sort((a, b) => (b.roi ?? 0) - (a.roi ?? 0));
        return {
          carrier: sortedByROI[0].carrier,
          topQuote: sortedByROI[0],
          allQuotes: sortedByROI,
          maritalClass: sortedByROI[0].maritalClass,
        };
      })
      .sort((a, b) => (b.topQuote.roi ?? 0) - (a.topQuote.roi ?? 0));
  }, [filteredTraditionalLTCQuotes]);

  // Combine all groups and limit to top 5 when in unselected mode
  const allGroups = useMemo(() => {
    const combined = [
      ...lifeInsuranceByCarrier.map(group => ({ ...group, type: 'life' as const })),
      ...traditionalLTCByCarrierAndMaritalClass.map(group => ({ ...group, type: 'ltc' as const })),
    ];

    // Sort all groups by ROI
    const sorted = combined.sort((a, b) => (b.topQuote.roi ?? 0) - (a.topQuote.roi ?? 0));

    // Limit to top 5 when in unselected mode
    if (filterMode === 'unselected') {
      return sorted.slice(0, 5);
    }

    return sorted;
  }, [lifeInsuranceByCarrier, traditionalLTCByCarrierAndMaritalClass, filterMode]);

  // Split groups back into life and ltc for rendering
  const limitedLifeInsuranceGroups = allGroups.filter(g => g.type === 'life');
  const limitedTraditionalLTCGroups = allGroups.filter(g => g.type === 'ltc');

  // Combine all quotes for empty state check
  const allQuotes = allGroups.map(g => g.topQuote);

  // Toggle policy - add or remove from multipleFundingSources immediately
  const handleTogglePolicy = (quote: QuoteWithROI | TraditionalLTCQuoteWithROI) => {
    if (!client?.clientId) return;

    const { isSelected, fundingSourceId } = isQuoteSelected(quote);

    if (isSelected && fundingSourceId) {
      // Show delete confirmation dialog, then remove the policy
      handleDeleteClick(() => {
        deletePolicyByFundingSourceIdCallBack(fundingSourceId);
      });
    } else {
      // Add the policy to multipleFundingSources
      addPolicy(quote);
    }
  };

  const handleToggleExpand = (quoteId: string) => {
    setExpandedCardId(expandedCardId === quoteId ? null : quoteId);
  };

  const handleCompare = () => {
    // Placeholder for future functionality
    console.log('Compare functionality not yet implemented');
  };

  // Show empty state if no quotes
  if (allQuotes.length === 0) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900">
            {emptyMessage?.title || 'No Quotes Available'}
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            {emptyMessage?.description ||
              "We couldn't find any quotes matching your profile at this time."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {limitedLifeInsuranceGroups.map(group => {
          if (group.type !== 'life') return null;
          const { topQuote, allQuotes, policyType } = group;
          const quoteId = getQuoteId(topQuote);
          return (
            <PolicyQuoteCard
              key={`${quoteId}-${policyType}`}
              quote={topQuote}
              allCarrierQuotes={allQuotes}
              isExpanded={expandedCardId === quoteId}
              isSelected={isQuoteSelected(topQuote).isSelected}
              onToggleExpand={() => handleToggleExpand(quoteId)}
              onCompare={handleCompare}
              onAddToPlan={handleTogglePolicy}
            />
          );
        })}
        {limitedTraditionalLTCGroups.map(group => {
          if (group.type !== 'ltc') return null;
          const { topQuote, allQuotes, maritalClass } = group;
          const quoteId = getQuoteId(topQuote);
          return (
            <PolicyQuoteCard
              key={`${quoteId}-${maritalClass}`}
              quote={topQuote}
              allCarrierQuotes={allQuotes}
              isExpanded={expandedCardId === quoteId}
              isSelected={isQuoteSelected(topQuote).isSelected}
              onToggleExpand={() => handleToggleExpand(quoteId)}
              onCompare={handleCompare}
              onAddToPlan={handleTogglePolicy}
            />
          );
        })}
      </div>
      <FundingSourceDeleteDialogs
        showDeleteConfirmation={showDeleteConfirmation}
        handleCancelDelete={handleCancelDelete}
        handleConfirmDelete={handleConfirmDelete}
        showDeleteBlockedDialog={showDeleteBlockedDialog}
        setShowDeleteBlockedDialog={setShowDeleteBlockedDialog}
        deleteBlockedReason={deleteBlockedReason}
        showJointPolicyDeleteDialog={showJointPolicyDeleteDialog}
        handleJointPolicyDeleteCancel={handleJointPolicyDeleteCancel}
        handleJointPolicyDeleteConfirm={handleJointPolicyDeleteConfirm}
        jointPolicyDeleteMode={jointPolicyDeleteMode}
        setJointPolicyDeleteMode={setJointPolicyDeleteMode}
        clientName={client.clientFirstName}
        partnerName={client.mutableClientPartner?.clientFirstName}
      />
    </>
  );
}
